import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, MessageCircle } from "lucide-react";

const MessageChart = ({ chartData }) => {
  // Calculate totals
  const totalMessages =
    chartData?.reduce((sum, item) => sum + (item.messages || 0), 0) || 0;
  const formattedTotal =
    totalMessages >= 1000
      ? `${(totalMessages / 1000).toFixed(1)}K`
      : totalMessages;
  const lastValue = chartData?.[chartData.length - 1]?.messages || 0;
  const secondLastValue = chartData?.[chartData.length - 2]?.messages || 0;
  const growth = ((lastValue - secondLastValue) / (secondLastValue || 1)) * 100;
  const isPositive = growth >= 0;
  const strokeColor = "#6366f1";

  return (
    <div className="glass-surface rounded-2xl border border-indigo-200/70 p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-gradient-to-br from-indigo-100 to-violet-100 p-1.5">
            <MessageCircle className="h-3.5 w-3.5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Message Analytics</h3>
            <p className="text-[11px] text-slate-500">Delivery overview</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-lg font-bold text-slate-900">{formattedTotal}</div>
            <div className="text-[11px] text-slate-500">Total</div>
          </div>
          <div
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] ${
              isPositive
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700"
            }`}
          >
            <TrendingUp className={`w-3 h-3 ${!isPositive && "rotate-180"}`} />
            {isPositive ? "+" : ""}
            {growth.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
          >
            <defs>
              <linearGradient id="messageGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e0e7ff"
              vertical={false}
            />

            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "#64748b" }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "#64748b" }}
              tickFormatter={(value) =>
                value >= 1000 ? `${value / 1000}K` : value
              }
            />

            <Tooltip
              contentStyle={{
                backgroundColor: "rgba(255,255,255,0.95)",
                border: "1px solid #c7d2fe",
                borderRadius: "8px",
                fontSize: "12px",
                boxShadow: "0 10px 24px -16px rgba(79,70,229,0.65)",
              }}
              formatter={(value) => [`${value} messages`, "Count"]}
              labelStyle={{ fontSize: "12px", fontWeight: 600 }}
            />

            <Area
              type="monotone"
              dataKey="messages"
              stroke={strokeColor}
              strokeWidth={2}
              fill="url(#messageGradient)"
              activeDot={{
                r: 4,
                fill: strokeColor,
                stroke: "white",
                strokeWidth: 2,
              }}
              animationDuration={1000}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-indigo-100 pt-2">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <div className="h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" />
          <span>Message trend</span>
        </div>
        <div className="text-[11px] text-slate-500">
          {chartData?.length || 12} months
        </div>
      </div>
    </div>
  );
};

export default MessageChart;
